import React from 'react';
import { Mail, Phone, Star, Trash2 } from 'lucide-react';
import { Button, PlusIcon } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  ContactPointKind,
  ContactPointRow,
  EMAIL_TYPE_OPTIONS,
  PHONE_TYPE_OPTIONS,
  emptyContactPointRow,
} from './contactPoints';
import { deleteActionClass, secondaryTextClass } from './themeHelpers';

interface ContactPointsEditorProps {
  kind: ContactPointKind;
  label: string;
  value: ContactPointRow[];
  onChange: (rows: ContactPointRow[]) => void;
  disabled?: boolean;
  showPersonFields?: boolean;
  /** Tighter layout when nested inside a contact card. */
  embedded?: boolean;
  /** Dense single-line rows for organization contact cards. */
  compact?: boolean;
  /** Hide label/add row — parent renders add control elsewhere. */
  hideHeader?: boolean;
  /** Stack rows in two columns on wider screens. */
  twoColumn?: boolean;
}

const ContactPointsEditor: React.FC<ContactPointsEditorProps> = ({
  kind,
  label,
  value,
  onChange,
  disabled = false,
  showPersonFields = true,
  embedded = false,
  compact = false,
  hideHeader = false,
  twoColumn = false,
}) => {
  const { colors } = useTheme();
  const rows = value.length > 0 ? value : [emptyContactPointRow(kind, true)];
  const typeOptions = kind === 'EMAIL' ? EMAIL_TYPE_OPTIONS : PHONE_TYPE_OPTIONS;
  const valueLabel = kind === 'EMAIL' ? 'Email address' : 'Phone number';
  const KindIcon = kind === 'EMAIL' ? Mail : Phone;
  const addLabel = kind === 'EMAIL' ? 'Add email' : 'Add phone';
  const dense = embedded || compact;
  const useTwoColumns = dense || twoColumn;

  const inputClassName = cn(
    'w-full rounded-md border transition-colors',
    dense ? 'h-8 px-2 text-sm' : 'h-9 px-3 text-sm',
    'focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    colors.border,
    embedded ? 'bg-white dark:bg-gray-950/60' : colors.surface,
    colors.text,
  );

  const updateRows = (nextRows: ContactPointRow[]) => {
    onChange(nextRows.length > 0 ? nextRows : [emptyContactPointRow(kind, true)]);
  };

  const updateRow = (index: number, patch: Partial<ContactPointRow>) => {
    const nextRows = rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row));
    updateRows(nextRows);
  };

  const setPrimary = (index: number) => {
    updateRows(rows.map((row, rowIndex) => ({ ...row, isPrimary: rowIndex === index })));
  };

  const removeRow = (index: number) => {
    const nextRows = rows.filter((_, rowIndex) => rowIndex !== index);
    if (nextRows.length > 0 && !nextRows.some((row) => row.isPrimary)) {
      nextRows[0] = { ...nextRows[0], isPrimary: true };
    }
    updateRows(nextRows);
  };

  const addRow = () => {
    updateRows([...rows, emptyContactPointRow(kind, rows.length === 0)]);
  };

  return (
    <div className={cn('min-w-0 w-full', !hideHeader && dense ? 'space-y-1.5' : hideHeader ? '' : 'space-y-3')}>
      {!hideHeader ? (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {!embedded ? (
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                  kind === 'EMAIL'
                    ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400'
                    : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400',
                )}
              >
                <KindIcon className="h-3.5 w-3.5" aria-hidden />
              </span>
            ) : null}
            <span
              className={cn(
                dense
                  ? 'text-[10px] font-semibold uppercase tracking-wider'
                  : 'text-sm font-semibold',
                colors.textSecondary,
              )}
            >
              {label}
            </span>
          </div>
          {!dense ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={disabled}
              title={addLabel}
              aria-label={addLabel}
              className="h-8 shrink-0 gap-1.5"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">{addLabel}</span>
            </Button>
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          useTwoColumns ? 'grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2' : 'space-y-2',
        )}
      >
        {rows.map((row, index) => (
          <div
            key={row.id ?? `row-${index}`}
            className={cn(
              dense ? 'min-w-0 rounded-md border px-2 py-1.5' : 'min-w-0 rounded-lg border p-2.5 sm:p-3',
              colors.border,
              !dense && (embedded
                ? 'bg-white/90 dark:bg-gray-950/40'
                : 'bg-gray-50/60 dark:bg-gray-900/30'),
              !dense && row.isPrimary && 'ring-1 ring-indigo-500/30 border-indigo-200 dark:border-indigo-900/50',
              dense && row.isPrimary && 'border-indigo-300 bg-indigo-50/50 dark:border-indigo-800 dark:bg-indigo-950/15',
            )}
          >
            {showPersonFields && kind === 'EMAIL' ? (
              <div className="mb-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  type="text"
                  value={row.contactName ?? ''}
                  onChange={(event) => updateRow(index, { contactName: event.target.value })}
                  placeholder="Name"
                  disabled={disabled}
                  aria-label="Contact name"
                  className={inputClassName}
                />
                <input
                  type="text"
                  value={row.contactPosition ?? ''}
                  onChange={(event) => updateRow(index, { contactPosition: event.target.value })}
                  placeholder="Position"
                  disabled={disabled}
                  aria-label="Contact position"
                  className={inputClassName}
                />
              </div>
            ) : null}

            <div
              className={cn(
                'grid gap-1.5',
                dense
                  ? 'grid-cols-[4.25rem_minmax(0,1fr)_auto_auto] items-center'
                  : useTwoColumns
                    ? 'grid-cols-[4.5rem_minmax(0,1fr)_auto_auto] items-center'
                    : 'grid-cols-1 sm:grid-cols-[6.5rem_minmax(0,1fr)_auto_auto] sm:items-center gap-2',
              )}
            >
              <select
                value={row.label}
                onChange={(event) => updateRow(index, { label: event.target.value })}
                disabled={disabled}
                aria-label="Type"
                className={cn(inputClassName, 'sm:w-full', dense && 'text-xs')}
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <input
                type={kind === 'EMAIL' ? 'email' : 'tel'}
                value={row.value}
                onChange={(event) => updateRow(index, { value: event.target.value })}
                placeholder={kind === 'EMAIL' ? 'name@example.com' : '+30 210 0000000'}
                disabled={disabled}
                aria-label={valueLabel}
                className={inputClassName}
              />

              <button
                type="button"
                onClick={() => setPrimary(index)}
                disabled={disabled}
              className={cn(
                'inline-flex items-center justify-center rounded-md border transition-colors',
                dense || useTwoColumns ? 'h-8 w-8' : 'h-9 gap-1 px-2.5 text-xs font-medium',
                  row.isPrimary
                    ? 'border-indigo-600 bg-indigo-600 text-white dark:border-indigo-500 dark:bg-indigo-600'
                    : cn(colors.border, colors.surface, secondaryTextClass(colors), 'hover:bg-gray-100 dark:hover:bg-gray-800'),
                )}
                title={row.isPrimary ? 'Primary contact' : 'Set as primary'}
              >
                <Star className={cn('h-3.5 w-3.5', row.isPrimary ? 'fill-current' : '')} aria-hidden />
                {!dense && !useTwoColumns ? <span className="hidden sm:inline">Primary</span> : null}
              </button>

              {rows.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRow(index)}
                  disabled={disabled}
                  title="Remove"
                  aria-label="Remove"
                  className={cn(
                    'shrink-0 justify-self-end sm:justify-self-auto',
                    dense || useTwoColumns ? 'h-8 w-8' : 'h-9 w-9',
                    deleteActionClass(colors),
                  )}
                >
                  <Trash2 className={cn(dense || useTwoColumns ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
                </Button>
              ) : dense || useTwoColumns ? null : (
                <div className="hidden sm:block" aria-hidden />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContactPointsEditor;
